'use client';

import { useEffect, useMemo, useState } from 'react';

import {
  useCreateServiceTypeMutation,
  useGetServiceGroupsQuery,
  useUpdateServiceTypeMutation,
} from '@/features/service-types/api/service-typesApi';
import { createServiceTypeSchema } from '@/features/service-types/schemas';
import { MainButton, SecondaryButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import { getBillableRateAmount } from '../utils/billableRate';

import styles from './ServiceTypeModalForm.module.scss';

import type { ServiceType } from '@web/types';

type ServiceTypeModalFormMode = 'create' | 'edit';

type ServiceTypeModalFormProps = {
  mode: ServiceTypeModalFormMode;
  serviceType?: ServiceType | null;
  onCancel?: () => void;
  onSuccess?: () => void;
};

type ServiceTypeFormState = {
  label: string;
  description: string;
  serviceGroupId: string;
  isBillable: boolean;
  billableRate: number | null;
};

function toFormState(serviceType?: ServiceType | null): ServiceTypeFormState {
  if (!serviceType) {
    return {
      label: '',
      description: '',
      serviceGroupId: 'ungrouped',
      isBillable: true,
      billableRate: 0,
    };
  }

  return {
    label: serviceType.label ?? '',
    description: serviceType.description ?? '',
    serviceGroupId: serviceType.service_group_id ?? 'ungrouped',
    isBillable: Boolean(serviceType.is_billable),
    billableRate: getBillableRateAmount(serviceType.billable_rate),
  };
}

function isValidState(values: ServiceTypeFormState) {
  if (!createServiceTypeSchema.shape.label.safeParse(values.label).success) {
    return false;
  }

  if (values.isBillable) {
    return values.billableRate !== null && values.billableRate >= 0;
  }

  return true;
}

export function ServiceTypeModalForm({
  mode,
  serviceType,
  onCancel,
  onSuccess,
}: ServiceTypeModalFormProps) {
  const [values, setValues] = useState<ServiceTypeFormState>(() =>
    toFormState(serviceType),
  );
  const [createServiceType, { isLoading: isCreating }] =
    useCreateServiceTypeMutation();
  const [updateServiceType, { isLoading: isUpdating }] =
    useUpdateServiceTypeMutation();
  const { data: serviceGroupsResponse } = useGetServiceGroupsQuery({
    page_size: 100,
    show_archived: false,
  });

  useEffect(() => {
    setValues(toFormState(serviceType));
  }, [serviceType]);

  const serviceGroupOptions = useMemo(
    () => [
      { value: 'ungrouped', label: 'No group' },
      ...((serviceGroupsResponse?.data?.results ?? []).map((group) => ({
        value: group.id,
        label: group.name,
      })) as Array<{ value: string; label: string }>),
    ],
    [serviceGroupsResponse?.data?.results],
  );

  const isValid = isValidState(values);
  const isLoading = isCreating || isUpdating;
  const submitLabel = mode === 'create' ? 'Create' : 'Save';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }

    const normalizedServiceGroupId =
      values.serviceGroupId === 'ungrouped' ? null : values.serviceGroupId;
    const normalizedBillableRate = values.isBillable
      ? values.billableRate
      : null;

    try {
      if (mode === 'create') {
        await createServiceType({
          label: values.label.trim(),
          description: values.description.trim(),
          billable_rate: normalizedBillableRate,
          is_billable: values.isBillable,
          service_group_id: normalizedServiceGroupId,
        }).unwrap();
        notify({ type: 'success', message: 'Service type created.' });
      } else if (serviceType) {
        await updateServiceType({
          serviceTypeId: serviceType.id,
          payload: {
            label: values.label.trim(),
            description: values.description.trim(),
            billable_rate: normalizedBillableRate,
            is_billable: values.isBillable,
            service_group_id: normalizedServiceGroupId,
          },
        }).unwrap();
        notify({ type: 'success', message: 'Service type updated.' });
      }

      onSuccess?.();
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form className={styles.serviceTypeModalForm} onSubmit={handleSubmit}>
      <SmartInput
        type="text"
        name="service-type-name"
        label="Name"
        value={values.label}
        onChange={(nextValue) =>
          setValues((current) => ({ ...current, label: nextValue }))
        }
        schema={createServiceTypeSchema.shape.label}
      />

      <SmartInput
        type="textarea"
        name="service-type-description"
        label="Description"
        value={values.description}
        onChange={(nextValue) =>
          setValues((current) => ({ ...current, description: nextValue }))
        }
      />

      <SmartInput
        type="select"
        name="service-type-service-group"
        label="Service group"
        value={values.serviceGroupId}
        options={serviceGroupOptions}
        onChange={(nextValue) =>
          setValues((current) => ({
            ...current,
            serviceGroupId: String(nextValue),
          }))
        }
      />

      <SmartInput
        type="checkbox"
        name="service-type-is-billable"
        label="Billable"
        checked={values.isBillable}
        onChange={(isBillable) =>
          setValues((current) => ({
            ...current,
            isBillable,
            billableRate: isBillable ? (current.billableRate ?? 0) : null,
          }))
        }
      />

      {values.isBillable ? (
        <>
          <SmartInput
            type="number"
            name="service-type-billable-rate"
            label="Base rate"
            value={values.billableRate}
            onChange={(nextValue) =>
              setValues((current) => ({ ...current, billableRate: nextValue }))
            }
            min={0}
            step={0.01}
            formatOnBlur
            asCurrency
          />

          <p className={styles.serviceTypeModalForm__rateHint}>
            Base rate is required when billable.
          </p>
        </>
      ) : null}

      <div className={styles.serviceTypeModalForm__actions}>
        <SecondaryButton
          ariaLabel="Cancel service type form"
          onClick={onCancel}
        >
          Cancel
        </SecondaryButton>
        <MainButton
          type="submit"
          ariaLabel={submitLabel}
          isLoading={isLoading}
          disabled={!isValid}
        >
          {submitLabel}
        </MainButton>
      </div>
    </form>
  );
}
